/**
 * Map a tool's `displayName` (or `toolName` fallback) to a gerund-phrase
 * activity label rendered in the assistant message while the AI is
 * running that tool. Mirrors the Cursor-style "Searching the knowledge
 * base...", "Reading past notes..." UX.
 *
 * Order of precedence:
 *   1. Exact match on the registered Marty tool `displayName`.
 *   2. Exact match on the underlying `toolName` (covers tools without a
 *      configured displayName).
 *   3. Fallback: append "..." to the displayName / toolName.
 *
 * Adding a new tool: just add an entry to `_DISPLAY_NAME_TO_GERUND` —
 * the fallback handles unmapped tools gracefully so this never blocks
 * shipping a new tool.
 */

const _DISPLAY_NAME_TO_GERUND: Record<string, string> = {
  "Search Knowledge Base": "Checking clinical documentation...",
  "Upcoming Appointments": "Looking up upcoming appointments...",
  "Past Appointments": "Looking up past appointments...",
  "Past Notes": "Reading past notes...",
  "Reschedule Appointment": "Preparing reschedule...",
  "Cancel Appointment": "Preparing cancellation...",
  "Search Providers": "Searching for providers...",
  "Update Therapist Status": "Preparing therapist update...",
  "Schedule Messages": "Drafting scheduled messages...",
};

const _TOOL_NAME_TO_GERUND: Record<string, string> = {
  searchKnowledgeBase: "Checking clinical documentation...",
  getUpcomingAppointments: "Looking up upcoming appointments...",
  getPastAppointmentsForClient: "Looking up past appointments...",
  getPastNotesForClient: "Reading past notes...",
  rescheduleAppointment: "Preparing reschedule...",
  cancelAppointment: "Preparing cancellation...",
  searchProviders: "Searching for providers...",
  updateTherapistStatus: "Preparing therapist update...",
  scheduleMessagesForStudent: "Drafting scheduled messages...",
};

export interface ActivityLabelInput {
  toolName: string;
  displayName?: string;
}

export function activityLabel({
  toolName,
  displayName,
}: ActivityLabelInput): string {
  if (displayName && _DISPLAY_NAME_TO_GERUND[displayName]) {
    return _DISPLAY_NAME_TO_GERUND[displayName];
  }
  if (_TOOL_NAME_TO_GERUND[toolName]) {
    return _TOOL_NAME_TO_GERUND[toolName];
  }
  return `${displayName ?? toolName}...`;
}

export const HITL_PAUSE_LABEL = (input: ActivityLabelInput): string =>
  `Awaiting confirmation: ${input.displayName ?? input.toolName}...`;

export const THINKING_LABEL = "Thinking...";
